import { apiHandler } from "./index";
import { endpoint } from "./endpoint";

/**
 * Normalizes API response based on the new backend envelope:
 * Success: { status: true, data: { ... } }
 * Error:   { status: false, message: "..." }
 */
const parseResponse = (res) => {
  const data = res?.data;
  const httpStatus = res?.status;

  // Handled network/offline error from apiHandler
  if (!data) {
    return {
      success: false,
      message: "Unable to connect to generation service. Please check your network.",
      status: httpStatus || 500,
    };
  }

  // If backend responded with explicit status: false or HTTP error >= 400
  if (data.status === false || (httpStatus && httpStatus >= 400)) {
    return {
      success: false,
      message: data.message || data.error_message || "Generation request failed.",
      status: httpStatus || (data.status === false ? 400 : 500),
      data: data.data || null,
    };
  }

  // Success response
  return {
    success: true,
    data: data.data !== undefined ? data.data : data,
    status: httpStatus || 200,
  };
};

// ==========================================
// AI MODEL GENERATION APIS
// ==========================================

/**
 * Queue a new AI model generation job.
 * POST /api/models/generate
 *
 * @param {Object} modelData
 * @param {string} modelData.name - Unique model name (required)
 * @param {"men"|"women"} modelData.gender - Target gender (required)
 * @param {string} [modelData.age]
 * @param {string} [modelData.ethnicity]
 * @param {string} [modelData.skinTone]
 * @param {string} [modelData.bodyType]
 * @param {Object|string} [modelData.hair] - { type, length, color } or string
 * @param {string} [modelData.clothing]
 * @param {string} [modelData.footwear]
 * @param {string} [modelData.background]
 * @param {string} [modelData.customPrompt]
 */
export const generateAIModel = async (modelData) => {
  try {
    const res = await apiHandler({
      url: endpoint.GENERATE_AI_MODEL,
      method: "POST",
      data: modelData,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to submit model generation.",
      status: 500,
    };
  }
};

/**
 * Fetch paginated AI model generation history with queue stats.
 * GET /api/models/generate/history
 *
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.status] - "all", "queued", "generating", "completed", "failed"
 * @param {string} [params.search]
 */
export const getAIModelHistory = async ({
  page = 1,
  limit = 10,
  status,
  search,
} = {}) => {
  try {
    const queryParams = { page, limit };
    if (status && status !== "all") {
      queryParams.status = status;
    }
    if (search && search.trim()) {
      queryParams.search = search.trim();
    }

    const res = await apiHandler({
      url: endpoint.GET_AI_MODEL_HISTORY,
      method: "GET",
      params: queryParams,
      noCache: true,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to fetch model history.",
      status: 500,
    };
  }
};

/**
 * Check single AI model job status.
 * GET /api/models/generate/status/:jobId
 */
export const getAIModelStatus = async (jobId) => {
  try {
    const res = await apiHandler({
      url: `${endpoint.GET_AI_MODEL_STATUS}/${jobId}`,
      method: "GET",
      noCache: true,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to fetch job status.",
      status: 500,
    };
  }
};

/**
 * Save completed AI model to store library.
 * POST /api/models/generate/:jobId/save-to-library
 *
 * @param {string} jobId
 * @param {Object} [options]
 * @param {string} [options.name]
 * @param {string} [options.tier="AI Studio"]
 * @param {"men"|"women"} [options.gender]
 * @param {boolean} [options.bIsActive=true]
 */
export const saveAIModelToLibrary = async (
  jobId,
  { name, tier = "AI Studio", gender, bIsActive = true } = {}
) => {
  try {
    const body = {};
    if (name) body.name = name;
    if (tier) body.tier = tier;
    if (gender) body.gender = gender;
    if (bIsActive !== undefined) body.bIsActive = bIsActive;

    const res = await apiHandler({
      url: `${endpoint.SAVE_AI_MODEL_TO_LIBRARY}/${jobId}/save-to-library`,
      method: "POST",
      data: body,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to save model to library.",
      status: 500,
    };
  }
};

/**
 * Delete model generation from history.
 * DELETE /api/models/generate/history/:jobId
 * Note: Generating jobs cannot be deleted and return 409.
 */
export const deleteAIModelHistory = async (jobId) => {
  try {
    const res = await apiHandler({
      url: `${endpoint.DELETE_AI_MODEL_HISTORY}/${jobId}`,
      method: "DELETE",
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to delete model history item.",
      status: 500,
    };
  }
};

// ==========================================
// AI BACKGROUND GENERATION APIS
// ==========================================

/**
 * Queue a new AI background generation job.
 * POST /api/backgrounds/generate
 *
 * @param {Object} bgData
 * @param {string} bgData.name - Unique background name (required)
 * @param {string} bgData.prompt - Prompt text (required)
 * @param {string} [bgData.themeId] - Static preset template ID (optional)
 * @param {string} [bgData.aspectRatio="3:4"] - Aspect ratio (optional)
 * @param {"men"|"women"|"unisex"} [bgData.gender="unisex"] - Target fitting (optional)
 */
export const generateAIBackground = async (bgData) => {
  try {
    const res = await apiHandler({
      url: endpoint.GENERATE_AI_BACKGROUND,
      method: "POST",
      data: bgData,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to submit background generation.",
      status: 500,
    };
  }
};

/**
 * Fetch paginated AI background generation history with queue stats.
 * GET /api/backgrounds/generate/history
 *
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.status] - "all", "queued", "generating", "completed", "failed"
 * @param {string} [params.search]
 */
export const getAIBgHistory = async ({
  page = 1,
  limit = 10,
  status,
  search,
} = {}) => {
  try {
    const queryParams = { page, limit };
    if (status && status !== "all") {
      queryParams.status = status;
    }
    if (search && search.trim()) {
      queryParams.search = search.trim();
    }

    const res = await apiHandler({
      url: endpoint.GET_AI_BG_HISTORY,
      method: "GET",
      params: queryParams,
      noCache: true,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to fetch background history.",
      status: 500,
    };
  }
};

/**
 * Check single AI background job status.
 * GET /api/backgrounds/generate/status/:jobId
 */
export const getAIBgStatus = async (jobId) => {
  try {
    const res = await apiHandler({
      url: `${endpoint.GET_AI_BG_STATUS}/${jobId}`,
      method: "GET",
      noCache: true,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to fetch background job status.",
      status: 500,
    };
  }
};

/**
 * Save completed AI background to store library.
 * POST /api/backgrounds/generate/:jobId/save-to-library
 *
 * @param {string} jobId
 * @param {Object} [options]
 * @param {string} [options.name]
 * @param {string} [options.tier="AI Studio"]
 * @param {"men"|"women"|"unisex"} [options.gender="unisex"]
 * @param {boolean} [options.bIsActive=true]
 */
export const saveAIBgToLibrary = async (
  jobId,
  { name, tier = "AI Studio", gender = "unisex", bIsActive = true } = {}
) => {
  try {
    const body = {};
    if (name) body.name = name;
    if (tier) body.tier = tier;
    if (gender) body.gender = gender;
    if (bIsActive !== undefined) body.bIsActive = bIsActive;

    const res = await apiHandler({
      url: `${endpoint.SAVE_AI_BG_TO_LIBRARY}/${jobId}/save-to-library`,
      method: "POST",
      data: body,
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to save background to library.",
      status: 500,
    };
  }
};

/**
 * Delete background generation from history.
 * DELETE /api/backgrounds/generate/history/:jobId
 * Note: Generating jobs cannot be deleted and return 409.
 */
export const deleteAIBgHistory = async (jobId) => {
  try {
    const res = await apiHandler({
      url: `${endpoint.DELETE_AI_BG_HISTORY}/${jobId}`,
      method: "DELETE",
    });
    return parseResponse(res);
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to delete background history item.",
      status: 500,
    };
  }
};
